<script context="module">
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA ;
    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import { writable } from 'svelte/store';
    import hierarchyOptions from '../notification/hierarchyOptions.js';
    import persistenceOptions from './persistenceOptions.js';

    const notifications = writable([]);

    let pagePersistentNotificationKeys = [];

    let sortByAutoclose = false;

    /**
     * Set options which will apply to all NotificationContainers
     *
     * @param {Object} options
     * @param {Boolean} [options.sortByAutoclose] - if true, autoclose notifications are sorted last
     */
    export function setNotificationContainerOptions(options = {}) {
        if (typeof options.sortByAutoclose !== 'undefined') {
            sortByAutoclose = options.sortByAutoclose;
        }
    }

    /**
     * Update notifications store, pushing a new notification with the received config.
     *
     * @param {Object} config - New notification config
     * @param {string} [config.title] - Notification title to display
     * @param {string} [config.message] - Notification message to display
     * @param {string} [config.hierarchy] - Notification type (alert, warning, neutral, success) - governs style & icon
     * @param {string} [config.data] - Notification data
     * @param {Object[]} [config.actions] - List of link or button attribute
     * @param {boolean} [config.closeable] - Displays the close icon
     * @param {Object} [config.closeButtonProps] - Props for closer iconBarButton
     * @param {string} [config.persistence] - Type of notification persistence (autoclose, pagePersistent, persistent)
     * @param {number} [config.timeout] - Applies to autoclose persistence type
     * @param {string} [persistenceOption] - Type of notification persistence (autoclose, pagePersistent, persistent)
     * @param {number} [timeoutOption] - Applies to autoclose persistence type
     *
     * @returns {string} - generated key of the notification
     */
    export function showNotification(
        {
            title,
            message,
            hierarchy = hierarchyOptions.neutral,
            data = {},
            actions = [],
            closeable = true,
            closeButtonProps = {
                icon: 'remove-12',
                size: 'base-12',
                label: __('close'),
                ariaLabel: __('close')
            },
            persistence = null,
            timeout = null
        },
        persistenceOption = persistenceOptions.autoclose,
        timeoutOption = 5000
    ) {
        const key = generateElementId('notification');

        persistence = persistence ?? persistenceOption;
        timeout = timeout ?? timeoutOption;

        notifications.update(oldNotifications => {
            oldNotifications.push({
                key,
                title,
                message,
                hierarchy,
                data,
                actions,
                closeable,
                closeButtonProps,
                persistence
            });

            if (sortByAutoclose) {
                const auto = persistenceOptions.autoclose;
                oldNotifications.sort((a, b) => {
                    if (a.persistence === auto && b.persistence !== auto) {
                        return 1;
                    }
                    if (a.persistence !== auto && b.persistence === auto) {
                        return -1;
                    }
                });
            }

            return oldNotifications;
        });

        if (persistence === persistenceOptions.autoclose) {
            setTimeout(() => {
                removeNotification(key);
            }, timeout);
        }
        if (persistence === persistenceOptions.pagePersistent) {
            pagePersistentNotificationKeys = [...pagePersistentNotificationKeys, key];
        }
        return key;
    }

    export function removeNotification(notificationKey) {
        notifications.update(oldNotifications => oldNotifications.filter(({ key }) => key !== notificationKey));
    }

    export function clearNotifications() {
        notifications.set([]);
    }

    const createNotification = (hierarchy, title, message, extraOptions = {}) => {
        if (typeof title === 'string' && typeof message === 'string') {
            return showNotification({ hierarchy, title, message, ...extraOptions });
        }

        if (typeof title === 'string' && (typeof message === 'object' || message === null)) {
            return showNotification({ hierarchy, title, ...(message ?? {}) });
        }

        return showNotification({ hierarchy, ...title });
    };

    export function showAlertNotification(title, message = null, options = {}) {
        return createNotification('alert', title, message, options);
    }

    export function showWarningNotification(title, message = null, options = {}) {
        return createNotification('warning', title, message, options);
    }

    export function showSuccessNotification(title, message = null, options = {}) {
        return createNotification('success', title, message, options);
    }

    export function showInfoNotification(title, message = null, options = {}) {
        return createNotification('neutral', title, message, options);
    }
</script>

<script>
    import Notification from './../notification/Notification.svelte';
    import { onDestroy, onMount } from 'svelte';

    function popStateListener() {
        do {
            const key = pagePersistentNotificationKeys.pop();
            removeNotification(key);
        } while (pagePersistentNotificationKeys.length > 0);
    }

    onMount(() => {
        window.addEventListener('popstate', popStateListener);
    });

    onDestroy(() => {
        window.removeEventListener('popstate', popStateListener);
    });
</script>

<style>
    .notification-container {
        position: absolute;
        z-index: calc(var(--layer-4) - 1);
        --spacing: var(--space-half);
    }

    .notification-container > :global(.notification-wrapper + .notification-wrapper) {
        margin-top: var(--spacing);
    }

    .notification-container > :global(div.notification-wrapper) {
        border-block-width: 0;
        border-inline-width: var(--space-half) 0;
    }

    /* larger than mobile */
    @media screen and (--mq-minwidth-small) {
        .notification-container {
            width: 54rem;
            inset-inline-end: 1rem;
            top: 1rem;
            --spacing: var(--space-1x);
        }
    }
    /* mobile */
    @media screen and (--mq-maxwidth-small) {
        .notification-container {
            width: auto;
            right: 0.5rem;
            left: 0.5rem;
            top: 0.5rem;
        }
    }
</style>

<div class="notification-container" role="log" aria-label={__('Notifications')}>
    {#each $notifications as { key, title, message, hierarchy, data, actions, closeable, closeButtonProps } (key)}
        <Notification
            {key}
            {title}
            {message}
            {hierarchy}
            {data}
            {actions}
            {closeable}
            {closeButtonProps}
            on:notificationclick
            on:notificationclose
            on:notificationclose={() => removeNotification(key)} />
    {/each}
</div>
